import { getAccessToken } from '../authentication/supabaseService';

const API_BASE = '/api/admin';

/**
 * The alert types the list can contain — the filter's dropdown, not a contract. New types are
 * added server-side as a new rule (the column is free text, code-enforced), so a type missing
 * here is still listed; it just renders its payload as raw JSON instead of prose.
 *
 * `report_held` is HISTORIC: the phase-2 send gate wrote it, phase 8 stopped holding reports
 * (they go out with a warning section instead), so nothing produces it any more. It stays here
 * because the old rows are still in the table and must remain findable.
 */
export const DATA_ALERT_TYPES = [
  'missing_revenue_day',
  'missing_supplier_week',
  'inventory_report_skipped',
  'report_held',
] as const;

/**
 * Translation key per alert type, shared by every surface that names one, so two surfaces can
 * never call the same type different things. A type without an entry falls back to its raw value.
 */
export const DATA_ALERT_TYPE_LABEL_KEYS: Record<string, string> = {
  missing_revenue_day: 'adminTesting.dataAlerts.typeMissingRevenueDay',
  missing_supplier_week: 'adminTesting.dataAlerts.typeMissingSupplierWeek',
  inventory_report_skipped: 'adminTesting.dataAlerts.typeInventoryReportSkipped',
  report_held: 'adminTesting.dataAlerts.typeReportHeld',
};

/** Takes a plain translator so this file stays free of an i18next dependency. */
export function dataAlertTypeLabel(
  alertType: string,
  translate: (key: string) => string
): string {
  const key = DATA_ALERT_TYPE_LABEL_KEYS[alertType];
  return key ? translate(key) : alertType;
}

/** scope_key the supplier detector uses when EVERY expected brand is zero — one alert, not N.
 *  Mirrors `ALL_SUPPLIERS_SCOPE_KEY` in server/domains/dataAlerts/dataAlertRules.ts. */
export const ALL_SUPPLIERS_SCOPE_KEY = 'ALL_SUPPLIERS';

/**
 * The compact "what is missing" fragment, beyond the type, period and restaurant every surface
 * already shows: the supplier brand, the brands the ALL_SUPPLIERS collapse stands in for, the
 * historic held report's type. Null when the type has nothing to add — `missing_revenue_day` is
 * restaurant-level and its period key IS the missing day.
 */
export function dataAlertScopeSummary(
  alert: Pick<DataAlertRow, 'alertType' | 'scopeKey' | 'payload'>,
  translate: (key: string, vars?: Record<string, unknown>) => string
): string | null {
  const payload = alert.payload ?? {};

  switch (alert.alertType) {
    case 'missing_revenue_day':
      return null;

    case 'missing_supplier_week': {
      if (alert.scopeKey !== ALL_SUPPLIERS_SCOPE_KEY) return alert.scopeKey || null;
      const brands = Array.isArray(payload.brands)
        ? payload.brands.filter((brand): brand is string => typeof brand === 'string')
        : [];
      return brands.length > 0
        ? translate('adminTesting.dataAlerts.scopeAllSuppliers', { brands: brands.join(', ') })
        : translate('adminTesting.dataAlerts.scopeAllSuppliersUnknown');
    }

    // scope_key is `<reportType>:<inventoryGroupId>` — a raw uuid tells nobody anything, so
    // the readable halves come out of the payload instead.
    case 'inventory_report_skipped': {
      const groupName = typeof payload.groupName === 'string' ? payload.groupName : '';
      const reportType = typeof payload.reportType === 'string' ? payload.reportType : '';
      if (!groupName && !reportType) return alert.scopeKey || null;
      return groupName && reportType ? `${reportType} · ${groupName}` : groupName || reportType;
    }

    case 'report_held':
      return typeof payload.reportType === 'string' && payload.reportType !== ''
        ? payload.reportType
        : alert.scopeKey || null;

    default:
      return alert.scopeKey || null;
  }
}

/** CHECK-constrained on the table, so this union is exhaustive. */
export const DATA_ALERT_STATUSES = ['open', 'resolved', 'auto_resolved'] as const;
export type DataAlertStatus = (typeof DATA_ALERT_STATUSES)[number];

/** `accepted` = the data really is missing, the user will send manually; `false_positive` = the
 *  rule fired on data that is fine. Resolving never sends anything either way. */
export const DATA_ALERT_RESOLUTION_KINDS = ['accepted', 'false_positive'] as const;
export type DataAlertResolutionKind = (typeof DATA_ALERT_RESOLUTION_KINDS)[number];

export interface DataAlertRow {
  id: string;
  alertType: string;
  restaurantId: string | null;
  /** Denormalized at detection time — it survives a restaurant deletion, unlike a join. */
  restaurantName: string;
  scopeKey: string;
  periodKey: string;
  status: DataAlertStatus;
  resolutionKind: DataAlertResolutionKind | null;
  resolutionNote: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  payload: Record<string, unknown>;
  detectedAt: string;
  lastCheckedAt: string;
}

async function authHeaders() {
  const token = await getAccessToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export interface DataAlertFilters {
  status?: DataAlertStatus;
  type?: string;
  /** Server-side filter; the admin page filters by the denormalized name instead, so that
   *  alerts of deleted restaurants stay findable. */
  restaurantId?: string;
}

/** Every alert matching the filters, newest first. */
export async function getDataAlerts(filters: DataAlertFilters = {}): Promise<DataAlertRow[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.restaurantId) params.set('restaurantId', filters.restaurantId);
  const query = params.toString();

  const res = await fetch(`${API_BASE}/data-alerts${query ? `?${query}` : ''}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch data alerts: ${res.statusText}`);
  }
  const data = await res.json();
  return data.alerts;
}

export interface DataAlertDetectorCounts {
  raised: number;
  autoResolved: number;
}

export interface DataAlertRunResult {
  missingRevenueDay: DataAlertDetectorCounts;
  missingSupplierWeek: DataAlertDetectorCounts;
}

/**
 * Run both detectors on demand — the same work the 13:00 cron does. Without a range the server
 * uses the daily run's own window; with one it sweeps those days instead. The server owns every
 * range rule (both-or-neither, start before end, end no later than yesterday, capped span) and
 * answers 400 with a message, which this surfaces verbatim as the thrown Error.
 */
export async function runDataAlertDetectors(
  range?: { startDate: string; endDate: string }
): Promise<DataAlertRunResult> {
  const res = await fetch(`${API_BASE}/data-alerts/run`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(range ?? {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to run the alert check: ${res.statusText}`);
  }
  const data = await res.json();
  return data.result;
}

/**
 * Re-run the detector for one alert, now. The daily job only re-checks a rolling window, so an
 * older alert whose data was backfilled late needs this to auto-resolve. `cleared` says whether
 * the condition is gone — when it is, the returned alert comes back `auto_resolved`.
 * Rejected with 400 for the historic `report_held` type: there is no detector behind it and no
 * condition left to re-check, so the UI never offers the button for those rows.
 */
export async function recheckDataAlert(
  id: string
): Promise<{ alert: DataAlertRow; cleared: boolean }> {
  const res = await fetch(`${API_BASE}/data-alerts/${id}/recheck`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to recheck alert: ${res.statusText}`);
  }
  return res.json();
}

/** Close an open alert. Never sends a report — the user sends manually from Send Reports. */
export async function resolveDataAlert(
  id: string,
  kind: DataAlertResolutionKind,
  note?: string
): Promise<DataAlertRow> {
  const res = await fetch(`${API_BASE}/data-alerts/${id}/resolve`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ kind, note }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to resolve alert: ${res.statusText}`);
  }
  const data = await res.json();
  return data.alert;
}
